import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Tracks page views and actions for Didacta-registered users only.
 * Checks once per session if the current user is a Didacta user,
 * then logs page_view events on route changes.
 */
export function useDidactaTracking() {
  const location = useLocation();
  const isDidactaUser = useRef<boolean | null>(null);
  const userId = useRef<string | null>(null);
  const lastTrackedPath = useRef<string>("");

  // Check once if this user is a Didacta registrant
  useEffect(() => {
    const checkDidactaUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        isDidactaUser.current = false;
        return;
      }
      userId.current = session.user.id;

      const { data } = await supabase
        .from("didacta_registrations")
        .select("id")
        .eq("user_id", session.user.id)
        .limit(1);

      isDidactaUser.current = (data && data.length > 0) || false;

      // Log login event
      if (isDidactaUser.current) {
        trackEvent("login", { path: location.pathname });
      }
    };

    checkDidactaUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track page views on route changes
  useEffect(() => {
    if (isDidactaUser.current && userId.current && location.pathname !== lastTrackedPath.current) {
      lastTrackedPath.current = location.pathname;
      trackEventInternal(userId.current, "page_view", {
        path: location.pathname,
        search: location.search || undefined,
      });
    }
  }, [location.pathname, location.search]);

  const trackEvent = useCallback((eventType: string, eventData?: Record<string, unknown>) => {
    if (isDidactaUser.current && userId.current) {
      trackEventInternal(userId.current, eventType, eventData);
    }
  }, []);

  return { trackEvent };
}

function trackEventInternal(
  currentUserId: string,
  eventType: string,
  eventData?: Record<string, unknown>
) {
  // Fire-and-forget, no await needed
  // Table not yet in generated types, use type assertion
  (supabase.from("didacta_user_events" as any) as any)
    .insert({
      user_id: currentUserId,
      event_type: eventType,
      event_data: eventData || {},
    })
    .then(({ error }) => {
      if (error) console.warn("[didacta-tracking]", error.message);
    });
}
